import z from "zod";
import type { ConversationItemTag } from "@remi/enums/conversation-item/tag";
import type { FormattedConversationItem } from "@remi/types/ai/conversation";

import { ErrorClassification } from "../../../sentry/classification";
import type { ErrorDetails } from "../domain";

const aiErrorTypeValues = [
  "UNKNOWN",
  "AI_SELECTED_PROJECT_NOT_IN_BID",
  "BID_NOT_FOUND",
  "TASK_MISSING_PROJECT",
  "TICKET_TOOL_PARSE_FAILED",
  "TICKET_TOOL_LINK_CREATION_FAILED",
  "USER_CONTEXT_REQUIRED",
  "AI_RUN_LINK_NOT_FOUND",
  "PROJECT_NOT_FOUND",
  "TICKET_NOT_FOUND",
  "ORGANIZATION_NOT_FOUND",
  "PHONE_CALL_NOT_FOUND",
  "CONVERSATION_ITEM_NOT_FOUND",
  "EMAIL_PERSON_NOT_FOUND",
  "COMMUNICATION_POLICY_VIOLATION_NOT_FOUND",
  "RAIN_SUMMARY_V1_DEPRECATED",
  "FAILED_TO_CREATE_CONVERSATION_ITEM_TAG",
  "GRAPH_NODE_MISSING_REQUIRED_STATE",
  "HOMEOWNER_EXPERIENCE_SCORE_DIR_NOT_FOUND",
  "HOMEOWNER_EXPERIENCE_INFERENCE_FAILED",
  "HOMEOWNER_SURVEY_SENTIMENT_FAILED",
  "RFQ_NOT_FOUND",
  "UNAUTHORIZED",
  "AI_SUMMARY_TIMEOUT",
  "QUOTING_INSIGHTS_REFRESH_COOLDOWN",
] as const;
const aiErrorTypeSchema = z.enum(aiErrorTypeValues);
const AiErrorType = aiErrorTypeSchema.enum;
type AiErrorType = z.infer<typeof aiErrorTypeSchema>;

export type AiErrors =
  | { errorType: typeof AiErrorType.UNKNOWN; data: undefined }
  | { errorType: typeof AiErrorType.AI_SELECTED_PROJECT_NOT_IN_BID; data: { projectId: number; bidId: number } }
  | { errorType: typeof AiErrorType.BID_NOT_FOUND; data: { bidId?: number; projectId?: number; taskId?: string } }
  | { errorType: typeof AiErrorType.TASK_MISSING_PROJECT; data: { taskId: string } }
  | { errorType: typeof AiErrorType.TICKET_TOOL_PARSE_FAILED; data: { outputs: string; runId: string; error: string } }
  | { errorType: typeof AiErrorType.TICKET_TOOL_LINK_CREATION_FAILED; data: { errors: string; runId: string } }
  | { errorType: typeof AiErrorType.USER_CONTEXT_REQUIRED; data: undefined }
  | { errorType: typeof AiErrorType.AI_RUN_LINK_NOT_FOUND; data: { aiRunLinkId: string } }
  | { errorType: typeof AiErrorType.PROJECT_NOT_FOUND; data: { projectId?: number; taskId?: string } }
  | { errorType: typeof AiErrorType.TICKET_NOT_FOUND; data: { ticketId: string } }
  | { errorType: typeof AiErrorType.ORGANIZATION_NOT_FOUND; data: { projectId: number } }
  | { errorType: typeof AiErrorType.PHONE_CALL_NOT_FOUND; data: { phoneCallId: string } }
  | { errorType: typeof AiErrorType.CONVERSATION_ITEM_NOT_FOUND; data: { conversationItemId: string } }
  | { errorType: typeof AiErrorType.EMAIL_PERSON_NOT_FOUND; data: { emailId: string; fromEmailAddress?: string } }
  | {
      errorType: typeof AiErrorType.COMMUNICATION_POLICY_VIOLATION_NOT_FOUND;
      data: { communication: FormattedConversationItem };
    }
  | { errorType: typeof AiErrorType.RAIN_SUMMARY_V1_DEPRECATED; data: { projectId: number; personId: string } }
  | {
      errorType: typeof AiErrorType.FAILED_TO_CREATE_CONVERSATION_ITEM_TAG;
      data: { conversationItemId: string; tags: ConversationItemTag[] };
    }
  | {
      errorType: typeof AiErrorType.GRAPH_NODE_MISSING_REQUIRED_STATE;
      data: { nodeName: string; requiredState: string };
    }
  | { errorType: typeof AiErrorType.HOMEOWNER_EXPERIENCE_SCORE_DIR_NOT_FOUND; data: { cwd: string } }
  | { errorType: typeof AiErrorType.HOMEOWNER_EXPERIENCE_INFERENCE_FAILED; data: { message: string; bidId: number } }
  | {
      errorType: typeof AiErrorType.HOMEOWNER_SURVEY_SENTIMENT_FAILED;
      data: {
        bidId: number;
        surveyFeedbackId: string;
        stage: string;
        errorName: string;
        errorMessage: string;
      };
    }
  | { errorType: typeof AiErrorType.RFQ_NOT_FOUND; data: { rfqId: number } }
  | { errorType: typeof AiErrorType.UNAUTHORIZED; data: { rfqId: number } }
  | { errorType: typeof AiErrorType.AI_SUMMARY_TIMEOUT; data: { projectId: number } }
  | {
      errorType: typeof AiErrorType.QUOTING_INSIGHTS_REFRESH_COOLDOWN;
      data: { rfqId: number; cooldownSecondsRemaining: number };
    };

export const aiErrorDetails: Record<AiErrorType, ErrorDetails> = {
  UNKNOWN: { classification: ErrorClassification.UNEXPECTED_ERROR, sentryDescription: "Unknown Error" },
  AI_SELECTED_PROJECT_NOT_IN_BID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "AI selected project not in bid",
  },
  BID_NOT_FOUND: { classification: ErrorClassification.NOT_FOUND_ERROR, sentryDescription: "Bid not found" },
  TASK_MISSING_PROJECT: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Task missing project",
  },
  TICKET_TOOL_PARSE_FAILED: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Ticket tool parse failed",
  },
  TICKET_TOOL_LINK_CREATION_FAILED: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Ticket tool link creation failed",
  },
  USER_CONTEXT_REQUIRED: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "User context required",
  },
  AI_RUN_LINK_NOT_FOUND: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "AI run link not found",
  },
  PROJECT_NOT_FOUND: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Project not found",
  },
  TICKET_NOT_FOUND: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Ticket not found",
  },
  ORGANIZATION_NOT_FOUND: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Organization not found",
  },
  PHONE_CALL_NOT_FOUND: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Phone call not found",
  },
  CONVERSATION_ITEM_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Conversation item not found",
  },
  EMAIL_PERSON_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Email person not found",
  },
  COMMUNICATION_POLICY_VIOLATION_NOT_FOUND: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Communication policy violation not found",
  },
  RAIN_SUMMARY_V1_DEPRECATED: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Rain summary v1 deprecated",
  },
  FAILED_TO_CREATE_CONVERSATION_ITEM_TAG: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Failed to create conversation item tag",
  },
  GRAPH_NODE_MISSING_REQUIRED_STATE: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Graph node missing required state",
  },
  HOMEOWNER_EXPERIENCE_SCORE_DIR_NOT_FOUND: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Homeowner experience score directory not found",
  },
  HOMEOWNER_EXPERIENCE_INFERENCE_FAILED: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Homeowner experience inference failed",
  },
  HOMEOWNER_SURVEY_SENTIMENT_FAILED: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Homeowner survey sentiment analysis failed",
  },
  RFQ_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "RFQ not found for AI insights",
  },
  UNAUTHORIZED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Unauthorized access to RFQ for AI insights",
  },
  AI_SUMMARY_TIMEOUT: {
    classification: ErrorClassification.TIMEOUT_ERROR,
    sentryDescription: "AI summary request timed out",
  },
  QUOTING_INSIGHTS_REFRESH_COOLDOWN: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Quoting insights refresh attempted within cooldown window",
  },
};
